import React from 'react';
import PropTypes from 'prop-types';

//semiotic
import { ResponsiveNetworkFrame } from 'semiotic';

//d3
import * as d3Scale from "d3-scale";
import * as d3Array from "d3-array";
import * as d3Interpolate from "d3-interpolate"

//lodash
import _ from 'lodash';

import { 
    DataBlick
  } from '../../variables';
  
//material ui
import { withStyles } from '@material-ui/core/styles';
import { stackedArea } from 'semiotic/lib/svg/lineDrawing';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';

// Minimum and maximum fallback sizes for markers
const MIN_MARKER_RADIUS = 1;
const MAX_MARKER_RADIUS = 25;

// hierarchyDataPreped
// (the original code contained a bug which would result
// in worldDataMissing always being an empty array)
function buildNetworkData(networkData, ConfigSourceField, ConfigTargetField, ConfigSourceColorField, ConfigTargetColorField, ConfigValueField) {
    //now that we are in here we can rename fields as we need to in order avoid errors
    _.forEach(networkData, (a) => {
        a['ConfigSourceField'] = ConfigSourceField;
        a['ConfigTargetField'] = ConfigTargetField;
        a['ConfigSourceColorField'] = ConfigSourceColorField;
        a['ConfigTargetColorField'] = ConfigTargetColorField;
        a['ConfigValueField'] = ConfigValueField;

        if (ConfigSourceField !== "source") {
            a['source'] = a[ConfigSourceField];
            delete a[ConfigSourceField];    
        }

        if (ConfigSourceColorField !== "sourceColor") {
            a['sourceColor'] = a[ConfigSourceColorField];
            delete a[ConfigSourceColorField];
        }
        
        if (ConfigTargetField !== "target") {
            a['target'] = a[ConfigTargetField];
            delete a[ConfigTargetField];
        }

        if (ConfigTargetColorField !== "targetColor") {
            if ( ConfigTargetColorField === ConfigSourceColorField ) {
                a['targetColor'] = a['sourceColor'];
            } else {
                a['targetColor'] = a[ConfigTargetColorField];
                delete a[ConfigTargetColorField];    
            }
        }

        if (ConfigValueField !== "value") {
            a['value'] = a[ConfigValueField];
            delete a[ConfigValueField];
        }
    });
    return networkData;
}

function buildNodeData (networkData) {
    if (!networkData) {
        return () => {};
    }

    //may need to figure out something other than cloneDeep as it is expensive
    const nodeDataSource = _.cloneDeep(networkData);
    const nodeDataTarget = _.cloneDeep(networkData);

    // first we start with the source fields
    _.forEach(nodeDataSource, (a) => {
        a['id'] = a.source;
        a['color'] = a["sourceColor"];
        // console.log('iterating on source node data', a);
    });

    // then we do the target fields
    _.forEach(nodeDataTarget, (a) => {
        a['id'] = a.target;
        a['color'] = a["targetColor"];
        // console.log('iterating on target node data', a);
    });

    // then we combine those into a unique list of nodes with id as the primary key
    const nodeData = _.map(_.uniqBy(_.union(nodeDataSource, nodeDataTarget), 'id'), o => { 
        let val = _.sumBy(_.filter(networkData, (a) => { return a.source === o.id || a.target === o.id }),"value");
        return {
            id: o.id, 
            color: o.color,
            value: val
        };
    });

    return nodeData;
}

function buildNodeSizeScale(nodeData, markerMinRadius, markerMaxRadius) {
    // console.log('build node size', nodeData, markerMinRadius, markerMaxRadius);
    if (!nodeData) {
      return () => {};
    }
    function remapper(d) {
      return parseFloat(d["value"] || 0) || 0;
    }
    return d3Scale.scaleSqrt()
      .domain(d3Array.extent(nodeData, remapper))
      .range([
        markerMinRadius*1 || MIN_MARKER_RADIUS*1,
        markerMaxRadius*1 || MAX_MARKER_RADIUS*1,
      ]);
}

function buildNodeColorScale(nodeData, nodeFillColor) {
    if (!nodeData) {
      return () => {};
    }
    function remapper(d) {
      return parseFloat(d["value"] || 0) || 0;
    }
    return d3Scale.scaleLinear()
      .domain(d3Array.extent(nodeData, remapper))
      .range(_.split(nodeFillColor,','))
    ;
}

// Create a memoized version of each call which will (hopefully) cache the calls.
// NOTE: passing the whole "props" to these functions will make them sub-optimal as
// the memoize depends on passing an equal object to get the cached result.
let memoized = {
    buildNetworkData: _.memoize(buildNetworkData),
    buildNodeData: _.memoize(buildNodeData),
    buildNodeSizeScale: _.memoize(buildNodeSizeScale),
    buildNodeColorScale: _.memoize(buildNodeColorScale),
};

class SemioticNetwork extends React.Component {
    constructor (props) {
      super(props);
      this.state = {
        edgeData: undefined,
        nodeData: undefined,
        nodeSizeScale: undefined,
        nodeColorScale: undefined
      }  
    }
    preprocessData() {
        const {
            data,
            tableauSettings,
        } = this.props;

        let networkData = memoized.buildNetworkData(
            data, 
            tableauSettings.ConfigSourceField,
            tableauSettings.ConfigTargetField,
            tableauSettings.ConfigSourceColorField,
            tableauSettings.ConfigTargetColorField,
            tableauSettings.ConfigValueField
        );

        let nodeData = memoized.buildNodeData(networkData);

        let nodeSizeScale = memoized.buildNodeSizeScale(nodeData, tableauSettings.markerMinRadius, tableauSettings.markerMaxRadius);
        let nodeColorScale = memoized.buildNodeColorScale(nodeData, tableauSettings.nodeFillColor);

        return ({
            edgeData: networkData, 
            nodeData: nodeData,
            nodeSizeScale: nodeSizeScale, 
            nodeColorScale: nodeColorScale,
        });
    }

    componentDidMount() {
        console.log('in semiotic component mount', this.props.data);
    }
  
    render() {
        console.log('semitoic component', this.props);
        const {
            height,
            width,
            data,
            networkType,
            networkProjection, 

            nodeSize,
            nodeRender,
            edgeRender,
            edgeType,

            nodeLabels,
            nodeColorConfig,
            nodeFillColor, 
            nodeFillOpacity, 
            nodeStrokeColor, 
            nodeStrokeOpacity,

            edgeColorConfig, 
            edgeFillOpacity, 
            edgeStrokeOpacity,
            edgeSize,

            highlightOn,
            hoverAnnotation,
            tableauSettings,        
        } = this.props;

        // pull in memoized stuff for use in render function
        let {
            edgeData, 
            nodeData,
            nodeSizeScale, 
            nodeColorScale,
        } = this.preprocessData();

        console.log('network Data in sub component', data, edgeData, nodeData); //, JSON.stringify(data));

        const popOver = (d) => {
            // console.log('in tooltip', d);
            if ( d.edge ) {
                return (
                    <Paper style={{'padding': '5px'}}>
                        <Typography> Edge: {d.source.id} -> {d.target.id} </Typography>
                        <Typography> {tableauSettings.ConfigValueField}: {d.value} </Typography>
                    </Paper>
                );
            } else {
                return (
                    <Paper style={{'padding': '5px'}}>
                        <Typography> Node: {d.id} </Typography>
                        <Typography> {tableauSettings.ConfigValueField}: {d.value} </Typography>
                    </Paper>
                );
            }
        }

        // we have an issue with color from tableau, the manipulated data doesn't carry the bound data forward. 
        // wonder if using nodes in addition to edges can help solve this. 
        // it should be assign color to node and pick either source or target color
        // or value basis, or solid color
        console.log('semiotic call', nodeData, data, tableauSettings);
        if ( !nodeData && !edgeData ) {
            return null;
        } else {
            return (
                <div className="semiotic-network" style={{ padding: '1%', height: height, width: width, float: 'none', margin: '0 auto' }}>
                    <ResponsiveNetworkFrame
                        //sizing 
                        responsiveHeight
                        responsiveWidth
                        margin={10}

                        // data
                        edges={edgeData}
                        nodes={nodeData}

                        // accessors
                        nodeIDAccessor={d => d.id}
                        sourceAccessor={d => d.source}
                        targetAccessor={d => d.target}

                        // network props
                        networkType={{
                            type: networkType,
                            projection: networkProjection,
                            forceManyBody: networkType === "force" ? -250 : -50,
                            edgeStrength: networkType === "force" ? 2 : 1,
                            iterations: networkType === "force" ? 1000 : 1,
                            distanceMax: networkType === "force" ? 500 : 1,
                            padAngle: parseFloat(tableauSettings.padAngle || 0.01),
                            nodePadding: 1,
                            padding: 0,
                        }}                

                        //size Accessors, type and render modes
                        edgeType={tableauSettings.edgeType}
                        edgeWidthAccessor={ networkType === "force" ? undefined : d => parseFloat(d.value||1)}
                        nodeSizeAccessor={ nodeSize === "value" ? d => nodeSizeScale(parseFloat(d.value)) : undefined }
                        nodeRenderMode={nodeRender}
                        edgeRenderMode={edgeRender}

                        // node styles
                        nodeLabels={nodeLabels}
                        nodeStyle={(d,i) => ( //console.log('node', d.index, d),
                        { 
                            fill: nodeColorConfig === "solid" ? _.split(nodeFillColor,',')[0]
                                : nodeColorConfig === "multi" ? _.split(nodeFillColor,',')[d.index % _.split(nodeFillColor,',').length]
                                : nodeColorConfig === "scale" && tableauSettings.ConfigValueField !== "None" ? nodeColorScale(d.value || 0)
                                : nodeColorConfig === "field" && tableauSettings.ConfigSourceColorField && tableauSettings.ConfigTargetColorField !== "None" ? d.color
                                : _.split(nodeFillColor,',')[0] || "#BDBDBD",
                            fillOpacity: parseFloat(nodeFillOpacity) || .4,
                            stroke: nodeColorConfig === "solid" ? _.split(nodeStrokeColor,',')[0]
                                : nodeColorConfig === "multi" ? _.split(nodeStrokeColor,',')[d.index % _.split(nodeStrokeColor,',').length]
                                : nodeColorConfig === "scale" && tableauSettings.ConfigValueField !== "None" ? nodeColorScale(d.value || 0)
                                : nodeColorConfig === "field" && tableauSettings.ConfigSourceColorField && tableauSettings.ConfigTargetColorField !== "None" ? d.color
                                : _.split(nodeStrokeColor,',')[0] || "#BDBDBD",
                            strokeOpacity: parseFloat(nodeStrokeOpacity) || .6,
                        })}

                        // edge styles
                        edgeStyle={(d,i) => ( //console.log('edge', d.source.index, d, _.split(nodeColor,',').length, i % _.split(nodeColor,',').length), 
                        {   fill: edgeColorConfig === "source" ? d.source.color : d.target.color,
                            fillOpacity: networkType === "force" ? 0 : edgeFillOpacity || .4,
                            stroke: edgeColorConfig === "source" ? d.source.color : d.target.color,
                            strokeOpacity: edgeStrokeOpacity || .6
                        })}
                    
                        // interactivity
                        hoverAnnotation={hoverAnnotation}
                        tooltipContent={d => popOver(d)}
                        customClickBehavior={(d) => this.props.clickCallBack(d)}
                        customHoverBehavior={(d) => this.props.hoverCallBack(d)}
                    />
                </div>
            );
        }
    }
}

// SemioticNetwork.propTypes = {
//     classes: PropTypes.object.isRequired,
// };
  
export default SemioticNetwork;
  


// WEBPACK FOOTER //
// ./src/components/SemioticNetwork/SemioticNetwork.js